import { useEffect, useRef, useState } from 'react';
import type { ComponentType, ReactNode, RefObject } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  ArrowUpRight,
  ChevronDown,
  FileText,
  Flag,
  Handshake,
  Loader2,
  Send,
  ThumbsDown,
  ThumbsUp,
  Wrench,
} from 'lucide-react';
import { toast } from 'sonner';
import type { Except } from 'type-fest';

import {
  GapCategory,
  GapFeedbackVote,
  GapTimelineEventKind,
  type DealIntelligenceGap,
  type DealIntelligenceProject,
  type GapEscalation,
  type GapEvidenceLink,
  type GapEvidenceRef,
  type GapProjectOverview,
  type GapRoiImpact,
} from '@momentum/types';

import {
  useAddGapComment,
  useDealIntelligence,
  useGapFeedback,
} from '@/shared/hooks/useDealIntelligence';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/Dialog';
import {
  parseInlineMarkdown,
  type InlineMarkdownToken,
} from '@/shared/utils/inline-markdown';

import {
  DealIntelligenceSidebar,
  SIDEBAR_WIDTH_CLASS,
} from './DealIntelligenceSidebar';

/**
 * Deal Intelligence - the live gap review feed. Every row is a real finding
 * the absence pipeline flagged on a live project; reviewers open a gap, read
 * the problem -> fix story, and rate how relevant it is for them.
 *
 * Page copy is intentionally English-only: the gap content itself is
 * English LLM output, so a bilingual chrome around it adds nothing yet.
 */

const FEED_WIDTH = 'mx-auto w-full max-w-5xl';
const DETAIL_WIDTH = 'mx-auto w-full max-w-[1080px]';

/**
 * Standard web-link affordance - brand blue + underline - so link text
 * reads as clickable at a glance. Shared by the project link, the analog
 * project links, and inline-markdown links.
 */
const LINK_CLASS =
  'text-[#0089bd] underline decoration-[#00AEEF]/50 underline-offset-2 transition-colors hover:decoration-[#0089bd]';

/**
 * Env-gated: whether the EN/LT gap-language toggle is offered. When unset,
 * the toggle is hidden and the page shows English only.
 */
const SHOW_LANGUAGE_TOGGLE =
  import.meta.env.VITE_SHOW_GAP_LANGUAGE_TOGGLE === 'true';

/** Display language for the gap texts - EN originals or LT translations. */
export type GapLanguage = 'en' | 'lt';

/**
 * A gap as the detail page reads it: what the API serves, except that
 * `severity` may be absent.
 *
 * Every gap in `gaps` has one. The gap-review preview renders a DRAFT, and no
 * agent writes a severity onto a draft - `source.impact` is a LOW/MEDIUM/HIGH
 * finder score on a different question - so the confidence readout has to be
 * able to say "not assigned" rather than band a missing number as Low.
 */
export type DetailGap = Except<DealIntelligenceGap, 'severity'> & {
  severity?: number;
};

const GAP_LANGUAGE_STORAGE_KEY = 'deal-intelligence:gap-language';

/**
 * The toggle selection survives reloads and navigation between the list and a
 * gap. Storage access is guarded because it throws under Safari private mode,
 * and anything other than a known language falls back to English.
 */
function storedGapLanguage(): GapLanguage {
  try {
    return localStorage.getItem(GAP_LANGUAGE_STORAGE_KEY) === 'lt'
      ? 'lt'
      : 'en';
  } catch {
    return 'en';
  }
}

function persistGapLanguage(language: GapLanguage): void {
  try {
    localStorage.setItem(GAP_LANGUAGE_STORAGE_KEY, language);
  } catch {
    // ignore
  }
}

/** Returns the translated text when LT is active and a translation exists. */
function localizedText(
  language: GapLanguage,
  englishText: string,
  lithuanianText: string | undefined
): string {
  return language === 'lt' && lithuanianText ? lithuanianText : englishText;
}

/**
 * Swaps every translated text field of a gap into the active language, so the
 * render tree below can stay language-agnostic. Structural data - ids, refs,
 * dates, event kinds, severities - always comes from the English base entry;
 * the LT entry supplies prose only, and its arrays are index-aligned with the
 * base arrays. Any field without a translation falls back to the English
 * original, so a partially translated gap renders as a clean mix.
 */
export function localizeGap(gap: DetailGap, language: GapLanguage): DetailGap {
  if (language === 'en' || !gap.lt) return gap;
  const translation = gap.lt;
  const text = (englishText: string, lithuanianText: string | undefined) =>
    localizedText(language, englishText, lithuanianText);

  return {
    ...gap,
    title: text(gap.title, translation.title),
    potentialImpact: gap.potentialImpact
      ? text(gap.potentialImpact, translation.potentialImpact)
      : gap.potentialImpact,
    fullProjectContext: gap.fullProjectContext
      ? text(gap.fullProjectContext, translation.fullProjectContext)
      : gap.fullProjectContext,

    projectOverview: {
      account: text(
        gap.projectOverview.account,
        translation.projectOverview?.account
      ),
      project: text(
        gap.projectOverview.project,
        translation.projectOverview?.project
      ),
      gapSummary: text(
        gap.projectOverview.gapSummary,
        translation.projectOverview?.gapSummary
      ),
    },

    roiImpact: gap.roiImpact && {
      value: text(gap.roiImpact.value, translation.roiImpact?.value),
      why: gap.roiImpact.why
        ? text(gap.roiImpact.why, translation.roiImpact?.why)
        : gap.roiImpact.why,
    },

    actionItems: gap.actionItems && {
      intro: gap.actionItems.intro
        ? text(gap.actionItems.intro, translation.actionItems?.intro)
        : gap.actionItems.intro,
      items: gap.actionItems.items.map((actionItem, itemIndex) => {
        const translatedItem = translation.actionItems?.items[itemIndex];
        return {
          text: text(actionItem.text, translatedItem?.text),
        };
      }),
    },

    escalation: gap.escalation && {
      ...gap.escalation,
      events: gap.escalation.events.map((escalationEvent, eventIndex) => {
        const translatedEvent = translation.escalation?.events[eventIndex];
        return {
          ...escalationEvent,
          dateLabel: text(
            escalationEvent.dateLabel,
            translatedEvent?.dateLabel
          ),
          title: text(escalationEvent.title, translatedEvent?.title),
          description: text(
            escalationEvent.description,
            translatedEvent?.description
          ),
          sources: escalationEvent.sources.map((source, sourceIndex) => ({
            ...source,
            label: text(
              source.label,
              translatedEvent?.sourceLabels?.[sourceIndex]
            ),
          })),
        };
      }),
    },

    seenBefore: gap.seenBefore && {
      projects: gap.seenBefore.projects.map(
        (seenBeforeProject, projectIndex) => {
          const translatedProject =
            translation.seenBefore?.projects[projectIndex];
          return {
            ...seenBeforeProject,
            name: text(seenBeforeProject.name, translatedProject?.name),
            description: text(
              seenBeforeProject.description,
              translatedProject?.description
            ),
          };
        }
      ),
    },
  };
}

/** Human-readable project title - the resolved name, or a short id fallback. */
function projectDisplayName(project: DealIntelligenceProject): string {
  return project.name ?? `Project ${project.externalId.slice(0, 8)}`;
}

/**
 * Renders the enrichment texts' inline markdown - `**bold**` and
 * `[label](url)` - as React nodes; everything else passes through as
 * plain text. The parse itself is shared with the internal gap-review
 * reader, which renders the same strings without the links - see
 * `shared/utils/inline-markdown.ts`.
 * With `plainLinks`, link labels render as emphasized text instead of
 * anchors - required inside the project cards, which are <button>s.
 */
const SEEN_BEFORE_HREF_PREFIX = 'seenbefore:';

interface RenderInlineMarkdownOptions {
  plainLinks?: boolean;
  /**
   * Handler for `[label](seenbefore:<id>)` tokens. `has` gates whether the id
   * resolves within the current gap; `onSelect` opens the past-project modal.
   * Unresolved tokens (or a missing handler) render as plain text, never a
   * dead link.
   */
  seenBefore?: { has: (id: string) => boolean; onSelect: (id: string) => void };
}

function renderInlineMarkdown(
  text: string,
  options?: RenderInlineMarkdownOptions
): ReactNode[] {
  return renderInlineTokens(parseInlineMarkdown(text), options);
}

function renderInlineTokens(
  tokens: InlineMarkdownToken[],
  options?: RenderInlineMarkdownOptions
): ReactNode[] {
  return tokens.map((token, tokenIndex) => {
    if (token.type === 'text') return token.text;

    if (token.type === 'bold') {
      return (
        <strong
          key={`bold-${tokenIndex}`}
          className="font-semibold text-[#0E1B3A]"
        >
          {renderInlineTokens(token.children, options)}
        </strong>
      );
    }

    if (options?.plainLinks) {
      return (
        <strong
          key={`link-${tokenIndex}`}
          className="font-medium text-[#0089bd]"
        >
          {token.label}
        </strong>
      );
    }

    const seenBeforeId = token.url.startsWith(SEEN_BEFORE_HREF_PREFIX)
      ? token.url.slice(SEEN_BEFORE_HREF_PREFIX.length)
      : null;

    if (seenBeforeId !== null) {
      // A past-project reference: open the modal rather than navigate. If the
      // id doesn't resolve in this gap, fall back to plain label text.
      if (!options?.seenBefore?.has(seenBeforeId)) return token.label;

      const openSeenBefore = options.seenBefore.onSelect;
      return (
        <button
          key={`seenbefore-${tokenIndex}`}
          type="button"
          onClick={(clickEvent) => {
            clickEvent.stopPropagation();
            openSeenBefore(seenBeforeId);
          }}
          className={`cursor-pointer font-medium ${LINK_CLASS}`}
        >
          {token.label}
        </button>
      );
    }

    return (
      <a
        key={`link-${tokenIndex}`}
        href={token.url}
        target="_blank"
        rel="noreferrer"
        onClick={(clickEvent) => clickEvent.stopPropagation()}
        className={`font-medium ${LINK_CLASS}`}
      >
        {token.label}
      </a>
    );
  });
}

/**
 * The project lede: the `account` and `project` narratives under their own
 * labels.
 *
 * A published gap always carries both — the schema requires them — but the
 * gap-review preview renders a DRAFT through this same component, and a run may
 * carry neither narrative. A blank one drops its label rather than printing a
 * heading over nothing.
 */
function ProjectOverviewLede({
  projectOverview,
  textClassName,
}: {
  projectOverview: GapProjectOverview;
  textClassName: string;
}): ReactNode {
  const labelClassName =
    'mb-1.5 block text-[11px] font-bold uppercase tracking-widest text-[#0E1B3A]/70';

  return (
    <>
      {projectOverview.account && (
        <div>
          <span className={labelClassName}>Account</span>
          <p className={textClassName}>
            {renderInlineMarkdown(projectOverview.account)}
          </p>
        </div>
      )}
      {projectOverview.project && (
        <div>
          <span className={labelClassName}>Project</span>
          <p className={textClassName}>
            {renderInlineMarkdown(projectOverview.project)}
          </p>
        </div>
      )}
    </>
  );
}

/* ─────────────────────────── Risk ranks ───────────────────────────── */

type GapRank = 'high' | 'medium' | 'low';

/** Collapses the 0..1 severity score into the three review ranks. */
function gapRank(severity: number): GapRank {
  if (severity >= 0.8) return 'high';
  if (severity >= 0.6) return 'medium';
  return 'low';
}

interface RankStyle {
  pillLabel: string;
  /** Filled bars in the confidence meter: 3 = high, 2 = medium, 1 = low. */
  activeBars: 1 | 2 | 3;
  pill: string;
  text: string;
}

const RANK_STYLES: Record<GapRank, RankStyle> = {
  high: {
    pillLabel: 'High',
    activeBars: 3,
    pill: 'border-rose-100 bg-transparent text-rose-600',
    text: 'text-rose-600',
  },
  medium: {
    pillLabel: 'Medium',
    activeBars: 2,
    pill: 'border-amber-100 bg-transparent text-amber-600',
    text: 'text-amber-600',
  },
  low: {
    pillLabel: 'Low',
    activeBars: 1,
    pill: 'border-zinc-200 bg-transparent text-zinc-600',
    text: 'text-zinc-600',
  },
};

/**
 * A three-bar confidence meter. Bars are bottom-aligned but the group is
 * centered in the 24×24 box, so it sits on the text's optical center inline.
 * Inactive bars fade to a quarter opacity, reading the level at a glance.
 */
function ConfidenceMeter({
  activeBars,
  className,
}: {
  activeBars: 1 | 2 | 3;
  className?: string;
}) {
  const bars = [
    { x: 3, height: 6 },
    { x: 9.5, height: 10 },
    { x: 16, height: 14 },
  ];
  const barBottom = 19;
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      {bars.map((bar, barIndex) => (
        <rect
          key={bar.x}
          x={bar.x}
          y={barBottom - bar.height}
          width={5}
          height={bar.height}
          rx={1.5}
          fill="currentColor"
          opacity={barIndex < activeBars ? 1 : 0.25}
        />
      ))}
    </svg>
  );
}

/** Borderless rank display for labeled contexts (the detail meta row). */
function RankInline({ rank }: { rank: GapRank }) {
  const rankStyle = RANK_STYLES[rank];
  return (
    <span className={`inline-flex items-center gap-1 ${rankStyle.text}`}>
      <ConfidenceMeter activeBars={rankStyle.activeBars} className="h-4 w-4" />
      <span className="text-sm font-bold">{rankStyle.pillLabel}</span>
    </span>
  );
}

/** Rank pill shown on feed and project cards for all three ranks. */
function RankPill({ rank }: { rank: GapRank }) {
  const rankStyle = RANK_STYLES[rank];
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 ${rankStyle.pill}`}
    >
      <ConfidenceMeter
        activeBars={rankStyle.activeBars}
        className="h-3.5 w-3.5"
      />
      <span className="text-[11px] font-bold uppercase tracking-wide">
        {rankStyle.pillLabel}
      </span>
    </span>
  );
}

/* ─────────────────────── Sales / delivery label ───────────────────── */

interface CategoryStyle {
  pillLabel: string;
  icon: ComponentType<{ className?: string }>;
  pill: string;
  text: string;
}

const CATEGORY_STYLES: Record<GapCategory, CategoryStyle> = {
  [GapCategory.SALES]: {
    pillLabel: 'Sales',
    icon: Handshake,
    pill: 'border-violet-100 bg-violet-50 text-violet-600',
    text: 'text-violet-600',
  },
  [GapCategory.DELIVERY]: {
    pillLabel: 'Delivery',
    icon: Wrench,
    pill: 'border-sky-100 bg-sky-50 text-sky-700',
    text: 'text-sky-700',
  },
};

/** Sales/delivery pill shown alongside the rank pill on gap cards. */
function CategoryPill({ category }: { category: GapCategory }) {
  const categoryStyle = CATEGORY_STYLES[category];
  const CategoryIcon = categoryStyle.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 ${categoryStyle.pill}`}
    >
      <CategoryIcon className="h-3.5 w-3.5" />
      <span className="text-[11px] font-bold uppercase tracking-wide">
        {categoryStyle.pillLabel}
      </span>
    </span>
  );
}

/** Borderless category display for labeled contexts (the detail meta row). */
function CategoryInline({ category }: { category: GapCategory }) {
  const categoryStyle = CATEGORY_STYLES[category];
  const CategoryIcon = categoryStyle.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 ${categoryStyle.text}`}>
      <CategoryIcon className="h-4 w-4" />
      <span className="text-sm font-bold">{categoryStyle.pillLabel}</span>
    </span>
  );
}

/* ─────────────────────────── Shared bits ──────────────────────────── */

function LanguageToggle({
  language,
  onChange,
}: {
  language: GapLanguage;
  onChange: (language: GapLanguage) => void;
}) {
  return (
    <div className="flex items-center gap-0.5 rounded-lg bg-zinc-200/50 p-1 text-xs font-semibold uppercase tracking-widest">
      {(['en', 'lt'] as const).map((languageOption) => (
        <button
          key={languageOption}
          type="button"
          onClick={() => onChange(languageOption)}
          className={`cursor-pointer rounded px-2.5 py-1 uppercase transition-colors ${
            language === languageOption
              ? 'bg-white text-[#0E1B3A] shadow-sm'
              : 'text-zinc-400 hover:text-[#0E1B3A]'
          }`}
        >
          {languageOption}
        </button>
      ))}
    </div>
  );
}

/** Quiet chip naming the gap's owning project. */
function ProjectChip({ project }: { project: DealIntelligenceProject }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-lg border border-zinc-100 bg-zinc-50 px-3 py-1.5 text-sm font-semibold">
      <span className="text-[#0E1B3A]">{projectDisplayName(project)}</span>
    </span>
  );
}

/* ─────────────────────── Feed (flat gap list) ─────────────────────── */

/** One gap paired with the project it was flagged on. */
interface FlatGap {
  gap: DealIntelligenceGap;
  project: DealIntelligenceProject;
}

/** All gaps across all projects, most severe first. */
function flattenGaps(projects: DealIntelligenceProject[]): FlatGap[] {
  return projects
    .flatMap((project) =>
      project.gaps.map((projectGap) => ({ gap: projectGap, project }))
    )
    .sort((firstEntry, secondEntry) => {
      return secondEntry.gap.severity - firstEntry.gap.severity;
    });
}

/**
 * Card surface: a hairline border plus a soft, deep drop shadow so the
 * white cards lift clearly off the near-white page. Includes the hover
 * lift + intensified shadow. Shared by the feed and project-page cards.
 */
const CARD_SURFACE =
  'border border-zinc-200/70 shadow-[0_12px_32px_-12px_rgba(14,27,58,0.20)] transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-14px_rgba(14,27,58,0.28)]';

/**
 * Marks the gap the reader just came back from: an outline sweeps in, pulses,
 * and fades out. The keyframes live in globals.css as `gap-highlight`.
 */
const CARD_HIGHLIGHT_RING =
  'outline outline-2 outline-offset-0 outline-transparent animate-gap-highlight';

/**
 * Matches the `gap-highlight` animation, so the class comes off only once the
 * outline has already faded to transparent.
 */
const GAP_HIGHLIGHT_DURATION_MS = 2400;

function GapCard({
  flatGap,
  language,
  onOpen,
  cardRef,
  isHighlighted,
}: {
  flatGap: FlatGap;
  language: GapLanguage;
  onOpen: (gapId: string) => void;
  /** Set only on the card the page scrolls back to. */
  cardRef?: RefObject<HTMLElement | null>;
  isHighlighted?: boolean;
}) {
  const { project } = flatGap;
  const gap = localizeGap(flatGap.gap, language);
  // Off the base entry, not the localized one: severity is structural, and only
  // the base entry types it as always-present.
  const rank = gapRank(flatGap.gap.severity);
  const gapText = gap.projectOverview.gapSummary;

  return (
    <button
      ref={cardRef as RefObject<HTMLButtonElement | null> | undefined}
      type="button"
      onClick={() => onOpen(gap._id)}
      className={`group relative flex w-full cursor-pointer flex-col rounded-3xl bg-white p-7 text-start ${CARD_SURFACE} ${
        isHighlighted ? CARD_HIGHLIGHT_RING : ''
      }`}
    >
      <span className="flex items-start justify-between gap-2.5">
        <span className="line-clamp-2 text-lg font-bold leading-snug text-[#0E1B3A]">
          {renderInlineMarkdown(gap.title, { plainLinks: true })}
        </span>
        <span className="flex shrink-0 items-center gap-1.5">
          <CategoryPill category={gap.category} />
          <RankPill rank={rank} />
        </span>
      </span>
      <span className="mt-2 line-clamp-3 text-sm leading-relaxed text-zinc-500">
        {/* plainLinks: the card itself is a <button>, so anchors can't nest. */}
        {renderInlineMarkdown(gapText, { plainLinks: true })}
      </span>

      <span className="mt-auto flex items-center pt-6">
        <ProjectChip project={project} />
      </span>
    </button>
  );
}

/* ────────────── Project page: summary line + story list ───────────── */

/** Count of high-rank gaps - surfaced in the project summary line. */
function highRankGapCount(flatGaps: FlatGap[]): number {
  return flatGaps.filter((flatGap) => gapRank(flatGap.gap.severity) === 'high')
    .length;
}

/** Zero-padded position label ("Gap 01 of 14") for the project story list. */
function gapPositionLabel(gapIndex: number, totalGapCount: number): string {
  return `Gap ${String(gapIndex + 1).padStart(2, '0')} of ${totalGapCount}`;
}

/**
 * The quiet one-line readout under the project header: the Intera logomark
 * plus a prose sentence stating how many gaps were found and how many are
 * high confidence. Deliberately a sentence between hairlines, not counter chips.
 */
function ProjectSummaryLine({ flatGaps }: { flatGaps: FlatGap[] }) {
  const totalGapCount = flatGaps.length;
  const highRankCount = highRankGapCount(flatGaps);

  return (
    <div className="mt-8 flex items-center gap-2.5 border-y border-zinc-200/80 py-3 text-[15px] text-zinc-600">
      <img src="/intera_logo_blue.svg" alt="" className="h-5 w-auto" />
      <span>
        Intera found{' '}
        <span className="font-bold text-[#0E1B3A]">
          {totalGapCount} execution {totalGapCount === 1 ? 'gap' : 'gaps'}
        </span>{' '}
        in this project
        {highRankCount > 0 && (
          <>
            {' - '}
            <span className="font-bold text-rose-600">
              {highRankCount} of them high confidence
            </span>
          </>
        )}
        .
      </span>
    </div>
  );
}

/**
 * Span-based mini version of SectionHairlineHeader for use inside the
 * project gap cards (the card is a <button>, so divs are off-limits).
 */
function CardSectionHeader({ title }: { title: string }) {
  return (
    <span className="flex items-center gap-3">
      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">
        {title}
      </span>
      <span className="h-px flex-1 bg-zinc-200/80" />
    </span>
  );
}

/**
 * The project page's gap card - a deliberately compact echo of the detail
 * page, carrying its two lead sections (Overview, Impact & actions) in the
 * same visual language, scaled down so a long list of gaps stays scannable.
 * Action steps show without their "Why" rationales - the full versions live
 * on the detail page. `seenbefore:` references in the impact prose open the
 * same project modal as the detail page.
 */
function ProjectGapCard({
  flatGap,
  gapIndex,
  totalGapCount,
  language,
  onOpen,
  cardRef,
  isHighlighted,
}: {
  flatGap: FlatGap;
  gapIndex: number;
  totalGapCount: number;
  language: GapLanguage;
  onOpen: (gapId: string) => void;
  /** Set only on the card the page scrolls back to. */
  cardRef?: RefObject<HTMLElement | null>;
  isHighlighted?: boolean;
}) {
  const gap = localizeGap(flatGap.gap, language);
  const rank = gapRank(flatGap.gap.severity);
  const gapText = gap.projectOverview.gapSummary;
  const impactText = gap.potentialImpact;
  const actionItems =
    gap.actionItems && gap.actionItems.items.length > 0
      ? gap.actionItems
      : undefined;
  // Resolve `seenbefore:<id>` tokens in the impact prose so a click opens the
  // project modal instead of falling through to the card's open-gap handler.
  const seenBeforeById = resolveSeenBeforeById(gap);
  const [selectedSeenBeforeId, setSelectedSeenBeforeId] = useState<
    string | null
  >(null);
  const selectedSeenBeforeProject = selectedSeenBeforeId
    ? seenBeforeById.get(selectedSeenBeforeId) ?? null
    : null;

  return (
    // A clickable div, not a <button>, so the "seen in past projects"
    // analog links can be real anchors (invalid nested inside a button).
    // The key handler only fires when the card itself is focused, so
    // activating an inner link doesn't also open the gap.
    <div
      ref={cardRef as RefObject<HTMLDivElement | null> | undefined}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(gap._id)}
      onKeyDown={(keyboardEvent) => {
        if (
          keyboardEvent.target === keyboardEvent.currentTarget &&
          (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ')
        ) {
          keyboardEvent.preventDefault();
          onOpen(gap._id);
        }
      }}
      className={`group flex w-full cursor-pointer flex-col gap-7 rounded-3xl bg-white p-7 text-start md:p-8 ${CARD_SURFACE} ${
        isHighlighted ? CARD_HIGHLIGHT_RING : ''
      }`}
    >
      <span className="flex w-full items-center justify-between gap-4">
        <span className="flex items-center gap-1.5">
          <RankPill rank={rank} />
          <CategoryPill category={gap.category} />
        </span>
        <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">
          {gapPositionLabel(gapIndex, totalGapCount)}
        </span>
      </span>

      <span className="block text-xl font-semibold leading-snug tracking-tight text-[#0E1B3A] md:text-2xl">
        {gap.title}
      </span>

      {/* Overview - the finding's cyan callout. The account/project context
          is shared for the whole project and shown once above all cards. */}
      <span className="flex w-full flex-col gap-4">
        <CardSectionHeader title="Overview" />
        <span className="relative block overflow-hidden rounded-xl border border-[#00AEEF]/15 bg-[#00AEEF]/[0.03] p-4">
          <span className="pointer-events-none absolute inset-0 rounded-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]" />
          <span className="relative z-10 block whitespace-pre-wrap text-[17px] leading-relaxed text-zinc-700">
            <span className="font-semibold text-[#0E1B3A]">The gap: </span>
            {renderInlineMarkdown(gapText, { plainLinks: true })}
          </span>
        </span>
      </span>

      {/* Impact & actions - impact prose plus the enriched Action Items list. */}
      {(impactText || actionItems) && (
        <span className="flex w-full flex-col gap-4">
          <CardSectionHeader title="Impact & actions" />
          {impactText && (
            <span className="block">
              <span className="block text-[11px] font-bold uppercase tracking-widest text-[#0E1B3A]/70">
                Potential impact
              </span>
              <span className="mt-1.5 block whitespace-pre-wrap text-[17px] leading-relaxed text-zinc-600">
                {renderInlineMarkdown(impactText, {
                  seenBefore: {
                    has: (id) => seenBeforeById.has(id),
                    onSelect: setSelectedSeenBeforeId,
                  },
                })}
              </span>
            </span>
          )}
          {actionItems && (
            <span className="relative block w-full overflow-hidden rounded-[20px] border border-zinc-300 bg-white shadow-md ring-1 ring-[#00AEEF]/10">
              <span className="absolute start-0 top-0 block h-[4px] w-full bg-[#00AEEF] opacity-80" />
              <span className="pointer-events-none absolute end-0 top-0 block h-[500px] w-[500px] bg-[radial-gradient(120%_120%_at_top_right,rgba(0,174,239,0.08)_0%,transparent_60%)]" />
              <span className="relative z-10 block p-6 lg:p-8">
                <span className="mb-8 flex items-center gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border bg-white shadow-sm ring-1 ring-[#0089bd]/20">
                    <img
                      src="/intera_logo_blue.svg"
                      alt=""
                      className="h-5 w-auto"
                    />
                  </span>
                  <span className="text-[20px] font-bold text-[#0E1B3A]">
                    INTERA Suggestions
                  </span>
                </span>
                {actionItems.intro && (
                  <span className="mb-4 block ps-1 text-[11px] font-bold uppercase tracking-widest text-[#0E1B3A]/70">
                    {renderInlineMarkdown(actionItems.intro, {
                      plainLinks: true,
                    })}
                  </span>
                )}
                <span className="flex flex-col gap-4">
                  {actionItems.items.map((actionItem, stepIndex) => (
                    <span
                      key={actionItem.text}
                      className="group flex items-start gap-4"
                    >
                      {actionItems.items.length > 1 && (
                        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#00AEEF]/10 text-xs font-bold text-[#0089bd] shadow-sm ring-1 ring-[#00AEEF]/20 transition-all group-hover:scale-[1.05] group-hover:bg-[#00AEEF]/20">
                          {stepIndex + 1}
                        </span>
                      )}
                      <span
                        className={`block text-[17px] leading-[1.6] text-[#0E1B3A] ${
                          actionItems.items.length > 1
                            ? 'font-semibold'
                            : 'font-normal'
                        }`}
                      >
                        {renderInlineMarkdown(actionItem.text, {
                          plainLinks: true,
                        })}
                      </span>
                    </span>
                  ))}
                </span>
              </span>
            </span>
          )}
        </span>
      )}

      <SeenBeforeProjectModal
        project={selectedSeenBeforeProject}
        onClose={() => setSelectedSeenBeforeId(null)}
      />
    </div>
  );
}

function GapFeed({
  flatGaps,
  scopedProject,
  language,
  onLanguageChange,
  onOpen,
  lastViewedGapId,
  lastViewedCardRef,
  highlightedGapId,
}: {
  flatGaps: FlatGap[];
  /** When set, the feed shows only this project's gaps under its name. */
  scopedProject?: DealIntelligenceProject;
  language: GapLanguage;
  onLanguageChange: (language: GapLanguage) => void;
  onOpen: (gapId: string) => void;
  /** The gap to scroll back to, and the ref the page scrolls with. */
  lastViewedGapId?: string;
  lastViewedCardRef: RefObject<HTMLElement | null>;
  /** Set while that card's ring is showing; cleared once it has faded. */
  highlightedGapId: string | null;
}) {
  // flatGaps is sorted most-severe first. The account/project context is the
  // same for every gap in the project, so it's shown once here from the lead
  // gap rather than repeated inside each card.
  const leadGap = flatGaps[0]?.gap;
  const sharedProjectOverview = leadGap
    ? localizeGap(leadGap, language).projectOverview
    : undefined;

  return (
    <div className={FEED_WIDTH}>
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          {scopedProject && (
            <Link
              to="/deal-intelligence"
              className="group mb-4 inline-flex items-center gap-2 text-sm font-semibold text-zinc-500 transition-colors hover:text-[#0E1B3A]"
            >
              <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1 rtl:rotate-180 rtl:group-hover:translate-x-1" />
              All gaps
            </Link>
          )}
          <h1 className="text-4xl font-extrabold tracking-tight text-[#0E1B3A] md:text-5xl">
            {scopedProject
              ? projectDisplayName(scopedProject)
              : 'Execution Gaps'}
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-zinc-500">
            {scopedProject
              ? 'Every execution gap Intera flagged on this project, with what to do about each one.'
              : 'Every execution gap Intera flagged across live projects - open a gap, read the finding, and rate how relevant it is for you.'}
          </p>
        </div>

        {SHOW_LANGUAGE_TOGGLE && (
          <LanguageToggle language={language} onChange={onLanguageChange} />
        )}
      </div>

      {scopedProject ? (
        <>
          <ProjectSummaryLine flatGaps={flatGaps} />
          {/* Shared account/project context for the whole project, from the
              highest-severity gap that carries an enriched overview. Rendered
              once here instead of inside every gap card. */}
          {sharedProjectOverview && (
            <div className="mt-8 flex flex-col gap-5 border-s-2 border-[#00AEEF] ps-5">
              <ProjectOverviewLede
                projectOverview={sharedProjectOverview}
                textClassName="text-[17px] font-light leading-relaxed text-zinc-600"
              />
            </div>
          )}
          <div className="mt-8 flex flex-col gap-6 pb-16">
            {flatGaps.map((flatGap, gapIndex) => (
              <ProjectGapCard
                key={flatGap.gap._id}
                flatGap={flatGap}
                gapIndex={gapIndex}
                totalGapCount={flatGaps.length}
                language={language}
                onOpen={onOpen}
                cardRef={
                  flatGap.gap._id === lastViewedGapId
                    ? lastViewedCardRef
                    : undefined
                }
                isHighlighted={flatGap.gap._id === highlightedGapId}
              />
            ))}
          </div>
        </>
      ) : (
        <div className="mt-10 grid gap-5 md:grid-cols-2">
          {flatGaps.map((flatGap) => (
            <GapCard
              key={flatGap.gap._id}
              flatGap={flatGap}
              language={language}
              onOpen={onOpen}
              cardRef={
                flatGap.gap._id === lastViewedGapId
                  ? lastViewedCardRef
                  : undefined
              }
              isHighlighted={flatGap.gap._id === highlightedGapId}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ──────────────── Detail: escalation presentation ─────────────────── */

/** Color families the escalation timeline uses to grade severity over time. */
type EscalationTone = 'zinc' | 'amber' | 'rose' | 'cyan';

/** How one escalation event kind renders: kicker label + tone + emphasis. */
interface EscalationKindStyle {
  label: string;
  tone: EscalationTone;
  /** Drawn larger with a halo ring on the time track. */
  isMajor?: boolean;
}

const ESCALATION_KIND_STYLES: Record<
  GapTimelineEventKind,
  EscalationKindStyle
> = {
  // A milestone is a neutral lifecycle anchor, not an alarm - it shares the
  // calm zinc tone and never gets the major emphasis reserved for incidents.
  [GapTimelineEventKind.MILESTONE]: { label: 'Milestone', tone: 'zinc' },
  [GapTimelineEventKind.EARLY_WARNING]: {
    label: 'Early warning',
    tone: 'zinc',
  },
  [GapTimelineEventKind.DRIFT]: { label: 'Drift', tone: 'amber' },
  [GapTimelineEventKind.INCIDENT]: {
    label: 'Incident',
    tone: 'rose',
    isMajor: true,
  },
  [GapTimelineEventKind.INTERA_FLAG]: { label: 'Intera flag', tone: 'cyan' },
  [GapTimelineEventKind.UPCOMING]: { label: 'Upcoming', tone: 'rose' },
};

/**
 * Evenly spaced position (4%..96%) for an event's node on the time track.
 * Even spacing, not date-proportional: many events carry only a coarse
 * dateLabel ("Apr 2025") with no precise date to interpolate from.
 */
function timelinePositionPercent(
  eventIndex: number,
  eventCount: number
): number {
  if (eventCount <= 1) return 50;
  return 4 + (eventIndex / (eventCount - 1)) * 92;
}

/** A node's spot on the time track, and whether it's an Intera-flag satellite. */
export interface EscalationNodeLayout {
  position: number;
  isFlag: boolean;
}

/**
 * Lays out the escalation nodes on the track. The "Intera flag" is not a real
 * step in the story - it's the moment Intera caught the gap - so it doesn't take
 * an evenly spaced slot. The primary events (everything else) own the even
 * spacing; each flag is a satellite planted right on the node before it (or the
 * node after it when the flag leads the timeline), so it always reads as "Intera
 * caught it here" pinned to that exact point.
 */
export function computeEscalationLayout(
  events: GapEscalation['events']
): EscalationNodeLayout[] {
  const primaryEventIndices = events
    .map((event, eventIndex) => ({ kind: event.kind, eventIndex }))
    .filter(({ kind }) => kind !== GapTimelineEventKind.INTERA_FLAG)
    .map(({ eventIndex }) => eventIndex);

  const positionByEventIndex = new Map<number, number>();
  primaryEventIndices.forEach((eventIndex, primaryOrder) => {
    positionByEventIndex.set(
      eventIndex,
      timelinePositionPercent(primaryOrder, primaryEventIndices.length)
    );
  });

  const primaryPositionBefore = (
    flagEventIndex: number
  ): number | undefined => {
    for (let scan = flagEventIndex - 1; scan >= 0; scan -= 1) {
      const found = positionByEventIndex.get(scan);
      if (found !== undefined) return found;
    }
    return undefined;
  };
  const primaryPositionAfter = (flagEventIndex: number): number | undefined => {
    for (let scan = flagEventIndex + 1; scan < events.length; scan += 1) {
      const found = positionByEventIndex.get(scan);
      if (found !== undefined) return found;
    }
    return undefined;
  };

  return events.map((event, eventIndex) => {
    if (event.kind !== GapTimelineEventKind.INTERA_FLAG) {
      return {
        position: positionByEventIndex.get(eventIndex) ?? 50,
        isFlag: false,
      };
    }

    // Pin the flag right on the node before it - the point where Intera caught
    // it. When the flag leads the timeline, pin it on the node after it instead.
    const positionBefore = primaryPositionBefore(eventIndex);
    if (positionBefore !== undefined) {
      return { position: positionBefore, isFlag: true };
    }
    const positionAfter = primaryPositionAfter(eventIndex);
    if (positionAfter !== undefined) {
      return { position: positionAfter, isFlag: true };
    }
    return { position: 50, isFlag: true };
  });
}

/**
 * BrightWork 365 and the CRM sales side share one Dynamics org; a ref's
 * entityType is the Dynamics entity name (etn). The enrichment stores
 * only the record identity, so the deep link is reconstructed here.
 */
const DYNAMICS_ORG_URL = 'https://bluebridge.crm4.dynamics.com';

/** Dynamics deep link to the external record an evidence ref points at. */
function dynamicsRefUrl(evidenceRef: GapEvidenceRef): string {
  return `${DYNAMICS_ORG_URL}/main.aspx?pagetype=entityrecord&etn=${evidenceRef.entityType}&id=${evidenceRef.externalId}`;
}

/** The evidence link's stored URL, or a Dynamics deep link built from its ref. */
function evidenceLinkUrl(evidenceLink: GapEvidenceLink): string | undefined {
  if (evidenceLink.url) return evidenceLink.url;
  return evidenceLink.ref ? dynamicsRefUrl(evidenceLink.ref) : undefined;
}

/**
 * Whether an evidence source points at a BlueBridge system - a BrightWork/
 * CRM ref or the BB SharePoint mirror. Only these show as evidence tags;
 * internal run reports, vendor docs and bare record-id labels stay out.
 */
function isBlueBridgeEvidence(evidenceLink: GapEvidenceLink): boolean {
  if (evidenceLink.ref) return true;
  return Boolean(evidenceLink.url?.includes('.sharepoint.com'));
}

/**
 * Badge chrome for an evidence source: the source system's logo asset
 * (BrightWork / Dynamics CRM / SharePoint), or a generic icon fallback.
 */
function evidenceSourceBadge(evidenceLink: GapEvidenceLink): {
  logoSrc?: string;
  icon?: ComponentType<{ className?: string }>;
  iconTone?: string;
} {
  if (evidenceLink.ref?.source === 'brightwork') {
    return { logoSrc: '/brightwork-logo.png' };
  }
  if (evidenceLink.ref) {
    return { logoSrc: '/dynamics-crm-logo.svg' };
  }
  if (
    evidenceLink.url?.includes('.sharepoint.com') ||
    /sharepoint/i.test(evidenceLink.label)
  ) {
    return { logoSrc: '/sharepoint-logo.svg' };
  }
  return { icon: FileText, iconTone: 'bg-[#00AEEF]/10 text-[#00AEEF]' };
}

/* ─────────────────── Detail: narrative sections ───────────────────── */

/** The editorial section header: 11px uppercase label + hairline rule. */
function SectionHairlineHeader({ title }: { title: string }) {
  return (
    <div className="mb-8 flex items-center gap-4">
      <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">
        {title}
      </h2>
      <span className="h-px flex-1 bg-zinc-200/80" />
    </div>
  );
}

/**
 * The "Is this correct?" row under the gap statement: white Correct / Incorrect
 * pills, plus the note modal they open. Voting records the vote immediately and
 * opens a modal where the reviewer can add an optional written note - submitted
 * notes are stored but never rendered back, so a thank-you toast is the only
 * acknowledgement.
 *
 * Its own component so the gap-review preview can render the claim card without
 * it: both mutations live here, so a card rendered with `interactive={false}`
 * never mounts a hook that could write against a draft id.
 */
function GapFeedbackRow({ gap }: { gap: DetailGap }) {
  const gapFeedbackMutation = useGapFeedback();
  const addCommentMutation = useAddGapComment();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [draftText, setDraftText] = useState('');

  const castVote = (vote: GapFeedbackVote) => {
    gapFeedbackMutation.mutate({
      gapId: gap._id,
      // Clicking the active vote clears it.
      vote: gap.myVote === vote ? null : vote,
    });
    setIsModalOpen(true);
  };

  const submitDraft = () => {
    if (addCommentMutation.isPending) return;
    const trimmedText = draftText.trim();
    // The vote is already recorded on the pill click; the note is optional, so
    // an empty submit just acknowledges the vote and closes.
    if (!trimmedText) {
      setIsModalOpen(false);
      toast.success('Thanks for your feedback!');
      return;
    }
    addCommentMutation.mutate(
      { gapId: gap._id, text: trimmedText },
      {
        onSuccess: () => {
          setIsModalOpen(false);
          setDraftText('');
          toast.success('Thanks for sharing your perspective!');
        },
      }
    );
  };

  const pillBase =
    'flex cursor-pointer items-center gap-1.5 rounded-full border px-4 py-2 text-[12px] font-semibold transition-all active:scale-95';

  return (
    <>
      <div className="relative z-10 mt-6 flex items-center justify-between border-t border-[#00AEEF]/15 pt-5">
        <span className="text-[13px] font-medium text-zinc-500">
          Is this correct?
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-pressed={gap.myVote === GapFeedbackVote.RELEVANT}
            onClick={() => castVote(GapFeedbackVote.RELEVANT)}
            className={`${pillBase} ${
              gap.myVote === GapFeedbackVote.RELEVANT
                ? 'border-emerald-600 bg-emerald-600 text-white shadow-sm'
                : 'border-zinc-200 bg-white text-[#0E1B3A] hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700'
            }`}
          >
            <ThumbsUp className="h-3.5 w-3.5" />
            Correct
          </button>
          <button
            type="button"
            aria-pressed={gap.myVote === GapFeedbackVote.NOT_RELEVANT}
            onClick={() => castVote(GapFeedbackVote.NOT_RELEVANT)}
            className={`${pillBase} ${
              gap.myVote === GapFeedbackVote.NOT_RELEVANT
                ? 'border-rose-600 bg-rose-600 text-white shadow-sm'
                : 'border-zinc-200 bg-white text-[#0E1B3A] hover:border-zinc-300 hover:bg-zinc-50'
            }`}
          >
            <ThumbsDown className="h-3.5 w-3.5" />
            Incorrect
          </button>
        </div>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Share your feedback</DialogTitle>
            <DialogDescription>
              Your note is anonymous and helps INTERA sharpen future signals.
            </DialogDescription>
          </DialogHeader>
          <textarea
            value={draftText}
            onChange={(changeEvent) => setDraftText(changeEvent.target.value)}
            rows={4}
            maxLength={2000}
            placeholder="What did we get right, or miss? (optional)…"
            className="w-full resize-none rounded-xl border border-zinc-200 bg-white p-3 text-[14px] text-[#0E1B3A] transition-all placeholder:text-zinc-400 focus:border-[#0089bd] focus:outline-none focus:ring-4 focus:ring-[#00AEEF]/10"
          />
          <DialogFooter>
            <button
              type="button"
              onClick={submitDraft}
              disabled={addCommentMutation.isPending}
              className="flex cursor-pointer items-center justify-center gap-1.5 rounded-full bg-[#0E1B3A] px-5 py-2.5 text-[13px] font-semibold text-white transition-all hover:bg-[#0E1B3A]/90 active:scale-95 disabled:cursor-not-allowed disabled:bg-zinc-200"
            >
              {addCommentMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Submit feedback
                  <Send className="h-3.5 w-3.5 rtl:-scale-x-100" />
                </>
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * The "The gap:" callout: the gap statement, with the feedback row under it.
 */
function GapClaimCard({
  gap,
  gapText,
  interactive,
}: {
  gap: DetailGap;
  gapText: string;
  interactive: boolean;
}) {
  return (
    <div className="relative overflow-hidden rounded-xl border border-[#00AEEF]/15 bg-[#00AEEF]/[0.03] p-7">
      <div className="pointer-events-none absolute inset-0 rounded-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]" />
      <p className="relative z-10 whitespace-pre-wrap text-[19px] leading-relaxed text-zinc-700">
        <span className="font-semibold text-[#0E1B3A]">The gap: </span>
        {renderInlineMarkdown(gapText)}
      </p>
      {interactive && <GapFeedbackRow gap={gap} />}
    </div>
  );
}

/**
 * The lede: the enriched project narrative reads as a cyan-ruled
 * pull-quote; "The gap" - the enriched gap statement - sits below it in a
 * cyan-tinted callout.
 */
function OverviewSection({
  gap,
  interactive,
}: {
  gap: DetailGap;
  interactive: boolean;
}) {
  const gapText = gap.projectOverview.gapSummary;

  const hasEscalation = Boolean(
    gap.escalation && gap.escalation.events.length > 0
  );

  return (
    <section>
      <SectionHairlineHeader title="Overview" />

      <div className="mb-6 space-y-5 border-s-2 border-[#00AEEF] ps-5">
        <ProjectOverviewLede
          projectOverview={gap.projectOverview}
          textClassName="text-lg font-light leading-relaxed text-zinc-600"
        />
      </div>

      {hasEscalation && gap.escalation && (
        <div className="mb-12">
          <EscalationSection escalation={gap.escalation} />
        </div>
      )}

      <GapClaimCard gap={gap} gapText={gapText} interactive={interactive} />
    </section>
  );
}

/**
 * The ROI readout that sits under the Potential impact prose: a bold headline
 * figure with an optional reasoning column explaining how the number was
 * reached. Rendered only when the gap carries a `roiImpact`.
 */
function PotentialImpactRoi({ roi }: { roi: GapRoiImpact }) {
  return (
    <div className="mt-6 overflow-hidden rounded-2xl border border-[#00AEEF]/20 bg-gradient-to-br from-[#00AEEF]/[0.05] to-transparent">
      <div className="flex flex-col gap-5 p-6 sm:flex-row sm:items-center sm:gap-8">
        <div className="shrink-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#0089bd]">
            Potential Money Loss
          </p>
          <p className="mt-1 text-[40px] font-bold leading-none text-[#0E1B3A]">
            {roi.value}
          </p>
          <p className="mt-2 max-w-[12rem] text-[12px] leading-snug text-zinc-500">
            estimated money at risk if this gap goes unresolved
          </p>
        </div>

        {roi.why && (
          <div className="sm:border-s sm:border-[#00AEEF]/15 sm:ps-8">
            <p className="mb-1.5 text-[11px] font-bold uppercase tracking-widest text-[#0E1B3A]/70">
              Why this number
            </p>
            <p className="text-[14px] leading-relaxed text-zinc-600">
              {renderInlineMarkdown(roi.why)}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/** A resolved past project ready to render in the modal (localized text). */
interface ResolvedSeenBeforeProject {
  name: string;
  description: string;
  ref?: GapEvidenceRef;
}

/**
 * Index a gap's `seenBefore` projects by their stable id, so `seenbefore:<id>`
 * tokens in the impact prose can resolve to a project for the modal. Expects a
 * gap already run through {@link localizeGap}, so the names and descriptions
 * are in the active language.
 */
function resolveSeenBeforeById(
  gap: DetailGap
): Map<string, ResolvedSeenBeforeProject> {
  const seenBeforeProjects = gap.seenBefore?.projects ?? [];
  const seenBeforeById = new Map<string, ResolvedSeenBeforeProject>();
  seenBeforeProjects.forEach((seenBeforeProject) => {
    if (!seenBeforeProject.id) return;
    seenBeforeById.set(seenBeforeProject.id, {
      name: seenBeforeProject.name,
      description: seenBeforeProject.description,
      ref: seenBeforeProject.ref,
    });
  });
  return seenBeforeById;
}

/**
 * Modal opened from a `seenbefore:` reference in the Potential impact prose:
 * the past project's name and description, plus a deep link to its CRM /
 * BrightWork record when a ref was stored.
 */
function SeenBeforeProjectModal({
  project,
  onClose,
}: {
  project: ResolvedSeenBeforeProject | null;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={project !== null}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent size="md">
        {project && (
          <>
            <DialogHeader>
              <DialogTitle>{project.name}</DialogTitle>
            </DialogHeader>
            <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-zinc-600">
              {project.description}
            </p>
            {project.ref && (
              <DialogFooter>
                <a
                  href={dynamicsRefUrl(project.ref)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#0E1B3A] px-5 py-2.5 text-[13px] font-semibold text-white transition-all hover:bg-[#0E1B3A]/90 active:scale-95"
                >
                  View in{' '}
                  {project.ref.source === 'brightwork' ? 'BrightWork' : 'CRM'}
                  <ArrowUpRight className="h-3.5 w-3.5 rtl:-scale-x-100" />
                </a>
              </DialogFooter>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * The impact paragraph and the Action Items card share one section: the
 * impact reads as full-width prose under a micro-label, and the enriched
 * checklist card below it is the page's most prominent module.
 */
function ImpactActionsSection({ gap }: { gap: DetailGap }) {
  const impactText = gap.potentialImpact;
  const actionItems = gap.actionItems;
  const hasActionItems = Boolean(actionItems && actionItems.items.length > 0);

  // Resolve `seenbefore:<id>` tokens in the impact prose to their project.
  const seenBeforeById = resolveSeenBeforeById(gap);
  const [selectedSeenBeforeId, setSelectedSeenBeforeId] = useState<
    string | null
  >(null);
  const selectedSeenBeforeProject = selectedSeenBeforeId
    ? seenBeforeById.get(selectedSeenBeforeId) ?? null
    : null;

  if (!impactText && !hasActionItems) return null;

  return (
    <section>
      <SectionHairlineHeader title="Impact & actions" />

      {impactText && (
        <>
          <p className="mb-3 text-[11px] font-bold uppercase tracking-widest text-[#0E1B3A]/70">
            Potential impact
          </p>
          <p className="whitespace-pre-wrap text-[17px] leading-relaxed text-zinc-600 md:text-[18px]">
            {renderInlineMarkdown(impactText, {
              seenBefore: {
                has: (id) => seenBeforeById.has(id),
                onSelect: setSelectedSeenBeforeId,
              },
            })}
          </p>
          {gap.roiImpact && <PotentialImpactRoi roi={gap.roiImpact} />}
        </>
      )}

      {actionItems && hasActionItems && (
        <div
          className={`relative overflow-hidden rounded-[20px] border border-zinc-300 bg-white shadow-md ring-1 ring-[#00AEEF]/10 ${
            impactText ? 'mt-8' : ''
          }`}
        >
          <div className="absolute start-0 top-0 h-[4px] w-full bg-[#00AEEF] opacity-80" />
          <div className="pointer-events-none absolute end-0 top-0 h-[500px] w-[500px] bg-[radial-gradient(120%_120%_at_top_right,rgba(0,174,239,0.08)_0%,transparent_60%)]" />

          <div className="relative z-10 p-6 lg:p-8">
            <div className="mb-8 flex items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border bg-white shadow-sm ring-1 ring-[#0089bd]/20">
                <img
                  src="/intera_logo_blue.svg"
                  alt=""
                  className="h-5 w-auto"
                />
              </div>
              <h3 className="text-[20px] font-bold text-[#0E1B3A]">
                INTERA Suggestions
              </h3>
            </div>

            {actionItems.intro && (
              <p className="mb-4 ps-1 text-[11px] font-bold uppercase tracking-widest text-[#0E1B3A]/70">
                {renderInlineMarkdown(actionItems.intro)}
              </p>
            )}

            <ol className="space-y-4">
              {actionItems.items.map((actionItem, stepIndex) => (
                <li
                  key={actionItem.text}
                  className="group flex items-start gap-4"
                >
                  {actionItems.items.length > 1 && (
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#00AEEF]/10 text-xs font-bold text-[#0089bd] shadow-sm ring-1 ring-[#00AEEF]/20 transition-all group-hover:scale-[1.05] group-hover:bg-[#00AEEF]/20">
                      {stepIndex + 1}
                    </span>
                  )}
                  <div>
                    <p
                      className={`text-[17px] leading-[1.6] text-[#0E1B3A] ${
                        actionItems.items.length > 1
                          ? 'font-semibold'
                          : 'font-normal'
                      }`}
                    >
                      {renderInlineMarkdown(actionItem.text)}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}

      <SeenBeforeProjectModal
        project={selectedSeenBeforeProject}
        onClose={() => setSelectedSeenBeforeId(null)}
      />
    </section>
  );
}

/** Per-tone styling for escalation event kickers (dot + label). */
const ESCALATION_KICKER_STYLES: Record<
  EscalationTone,
  { text: string; dot: string }
> = {
  zinc: { text: 'text-zinc-400', dot: 'bg-zinc-300' },
  amber: { text: 'text-amber-600/80', dot: 'bg-amber-500' },
  rose: { text: 'text-rose-600', dot: 'bg-rose-500' },
  cyan: { text: 'text-[#0089bd]', dot: 'bg-[#00AEEF]' },
};

/** Label colors for the dates under the timeline nodes. */
const TIMELINE_LABEL_STYLES: Record<EscalationTone, string> = {
  zinc: 'text-zinc-400',
  amber: 'text-amber-600',
  rose: 'text-rose-600',
  cyan: 'text-[#0089bd]',
};

/** Hex per tone - shared by the dots and the track so they match exactly. */
const ESCALATION_TONE_HEX: Record<EscalationTone, string> = {
  zinc: '#cbd5e1',
  amber: '#f59e0b',
  rose: '#e11d48',
  cyan: '#00aeef',
};

/**
 * Builds the track's gradient from the events' own tone colors at their
 * positions, so the line under each node is that node's color (calm →
 * hot follows the actual event sequence, not a fixed severity ramp). The
 * Intera-flag satellites are skipped - the line grades by the real story
 * events, and the flag's cyan lives in its own marker above the track. The
 * line element is flipped with rtl:-scale-x-100, so `to right` stays aligned
 * with the mirrored node positions in RTL.
 */
function escalationTrackGradient(
  events: GapEscalation['events'],
  layout: EscalationNodeLayout[]
): string {
  const stops = events
    .map((escalationEvent, eventIndex) => {
      const nodeLayout = layout[eventIndex];
      if (!nodeLayout || nodeLayout.isFlag) return undefined;
      const toneColor =
        ESCALATION_TONE_HEX[ESCALATION_KIND_STYLES[escalationEvent.kind].tone];
      return `${toneColor} ${nodeLayout.position}%`;
    })
    .filter((stop): stop is string => stop !== undefined);
  // Zero or one stop is not a valid gradient - repeat the last into a solid fill.
  const gradientStops =
    stops.length >= 2 ? stops : stops.concat(stops.length === 1 ? stops : []);
  if (gradientStops.length === 0) return 'none';
  return `linear-gradient(to right, ${gradientStops.join(', ')})`;
}

/** One dot on the time track, the event's date label underneath. */
function EscalationTimelineNodeMark({
  positionPercent,
  dateLabel,
  kindStyle,
}: {
  positionPercent: number;
  dateLabel: string;
  kindStyle: EscalationKindStyle;
}) {
  // The dot wears its own kind color (matching the date label); the track
  // gradient is built from these same colors so the line matches beneath.
  const dotColor = ESCALATION_TONE_HEX[kindStyle.tone];
  return (
    <div
      className={`absolute top-1/2 z-10 flex -translate-x-1/2 -translate-y-1/2 justify-center rtl:translate-x-1/2 ${
        kindStyle.isMajor ? 'h-6 w-6' : 'h-4 w-4'
      }`}
      style={{ insetInlineStart: `${positionPercent}%` }}
    >
      {kindStyle.isMajor ? (
        <div
          className="m-auto h-5 w-5 rounded-full shadow-sm ring-4 ring-white"
          style={{ backgroundColor: dotColor }}
        />
      ) : (
        <div
          className="m-auto h-3.5 w-3.5 rounded-full ring-2 ring-white"
          style={{ backgroundColor: dotColor }}
        />
      )}
      <span
        className={`absolute whitespace-nowrap text-[10px] font-bold uppercase tracking-wider ${
          kindStyle.isMajor ? 'top-[26px]' : 'top-6'
        } ${TIMELINE_LABEL_STYLES[kindStyle.tone]}`}
      >
        {dateLabel}
      </span>
    </div>
  );
}

/**
 * The Intera logomark (person + infinity) as two fill paths in its own
 * 214x278 glyph box - drawn white on the flag cloth. Mirrors
 * public/intera_favicon.svg so the flag carries the real brand mark.
 */
const INTERA_MARK_HEAD_PATH =
  'M 102.594 14.057 C 91.616 15.896, 77.956 25.307, 71.480 35.494 C 58.226 56.345, 61.027 83.242, 78.240 100.408 C 101.859 123.961, 138.466 117.237, 153.077 86.661 C 161.368 69.311, 160.404 52.280, 150.211 36 C 143.204 24.810, 130.199 16.074, 117.438 13.984 C 110.763 12.891, 109.519 12.897, 102.594 14.057';
const INTERA_MARK_INFINITY_PATH =
  'M 54.500 131.698 C 38.856 133.776, 23.736 145.296, 16.456 160.685 C 11.799 170.528, 10.758 180.271, 11.241 209.488 C 11.623 232.592, 11.901 236.125, 13.726 241.087 C 18.299 253.524, 24.228 261.766, 33.055 267.958 C 47.486 278.080, 63.803 279.547, 79.463 272.130 C 87.419 268.362, 89.691 266.397, 108.278 247.208 C 126.421 228.478, 125.789 231.591, 113.930 219.360 L 106.617 211.818 89.059 229.906 C 77.356 241.961, 70.260 248.495, 67.782 249.497 C 55.681 254.389, 40.888 246.057, 37.033 232.177 C 36.468 230.146, 36.009 217.726, 36.006 204.432 C 35.999 175.457, 36.643 172.262, 43.969 164.936 C 52.314 156.591, 64.157 154.927, 72.942 160.867 C 75.084 162.315, 79.686 166.650, 83.168 170.500 C 114.006 204.590, 135.591 227.189, 139.724 229.710 C 147.072 234.194, 152.845 236.139, 160.538 236.722 C 173.968 237.742, 185.995 233.272, 195.084 223.884 C 215.288 203.016, 214.610 166.405, 193.631 145.454 C 174.015 125.864, 145.344 126.767, 124.480 147.631 L 117.930 154.181 121.639 158.340 C 131.358 169.240, 136 174.032, 136 173.165 C 136 171.254, 145.393 161.839, 149.387 159.745 C 155.012 156.798, 163.994 156.801, 169.628 159.753 C 175.440 162.799, 181.711 170.289, 183.505 176.328 C 190.046 198.356, 173.929 218.778, 157.127 209.750 C 154.349 208.257, 143.338 197.065, 124.415 176.500 C 108.727 159.450, 94.003 143.977, 91.695 142.116 C 81.639 134.005, 67.323 129.995, 54.500 131.698';

/**
 * The Intera-flag marker: the moment Intera caught the gap. Not a step in the
 * story, so it doesn't wear a date - it's a swallowtail flag carrying the Intera
 * mark, planted on the track right after the node before it, reading "Intera
 * found it here". The anchor dot sits on the line with a soft detection ping;
 * the flag's pole is centered on the point and its cloth sways in the wind
 * (flipped in RTL so it flies the right way).
 */
function EscalationInteraFlagMark({
  positionPercent,
}: {
  positionPercent: number;
}) {
  return (
    <div
      className="absolute top-1/2 z-20 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rtl:translate-x-1/2"
      style={{ insetInlineStart: `${positionPercent}%` }}
    >
      {/* soft "detection" ping + anchor dot, centered on the point, on the line */}
      <span
        className="absolute inset-0 m-auto h-3.5 w-3.5 animate-ping rounded-full opacity-50"
        style={{ backgroundColor: '#00aeef' }}
      />
      <span
        className="absolute inset-0 z-10 m-auto h-2 w-2 rounded-full ring-2 ring-white"
        style={{ backgroundColor: '#00aeef' }}
      />
      {/* swallowtail flag above the line, pole centered over the anchor */}
      <div className="absolute bottom-1/2 left-1/2 -translate-x-1/2 rtl:-scale-x-100">
        <svg
          width="46"
          height="38"
          viewBox="0 0 140 116"
          fill="none"
          className="block"
          aria-hidden="true"
        >
          {/* pole planted on the anchor dot, with a rounded finial */}
          <rect x="68" y="6" width="4" height="110" rx="2" fill="#0089bd" />
          <circle cx="70" cy="6" r="4" fill="#0089bd" />
          {/* cloth + Intera mark, swaying together from the pole edge */}
          <g className="animate-flag-windy">
            <path d="M71 15 H131 L120.5 37 L131 59 H71 Z" fill="#00aeef" />
            <svg x="80" y="20" width="34" height="34" viewBox="5 8 214 278">
              <path fill="#ffffff" d={INTERA_MARK_HEAD_PATH} />
              <path fill="#ffffff" d={INTERA_MARK_INFINITY_PATH} />
            </svg>
          </g>
        </svg>
      </div>
    </div>
  );
}

/**
 * The escalation module: a time track whose gradient is built from the
 * events' own kind colors, one node per enriched event over a grid of the
 * dated events, each tagged with the evidence sources it was corroborated
 * from.
 */
function EscalationSection({ escalation }: { escalation: GapEscalation }) {
  const escalationLayout = computeEscalationLayout(escalation.events);
  return (
    <section>
      <div className="relative overflow-hidden rounded-[32px] border border-zinc-100 bg-white p-8 shadow-[0_24px_60px_-52px_rgba(16,27,58,0.5)] md:p-10">
        <h2 className="mb-8 text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">
          How it escalated
        </h2>

        <div className="relative mb-16 mt-4 h-6 w-full px-4">
          <div
            className="absolute inset-x-0 top-1/2 z-0 h-[2px] -translate-y-1/2 rounded-full rtl:-scale-x-100"
            style={{
              backgroundImage: escalationTrackGradient(
                escalation.events,
                escalationLayout
              ),
            }}
          />
          {escalation.events.map((escalationEvent, eventIndex) => {
            const nodeLayout = escalationLayout[eventIndex];
            if (!nodeLayout) return null;
            const nodeKey = `${escalationEvent.dateLabel}-${escalationEvent.title}`;
            return nodeLayout.isFlag ? (
              <EscalationInteraFlagMark
                key={nodeKey}
                positionPercent={nodeLayout.position}
              />
            ) : (
              <EscalationTimelineNodeMark
                key={nodeKey}
                positionPercent={nodeLayout.position}
                dateLabel={escalationEvent.dateLabel}
                kindStyle={ESCALATION_KIND_STYLES[escalationEvent.kind]}
              />
            );
          })}
        </div>

        <hr className="mb-8 border-zinc-100" />

        <div className="grid grid-cols-1 gap-x-6 gap-y-10 md:grid-cols-2 lg:grid-cols-3">
          {escalation.events.map((escalationEvent) => {
            const kindStyle = ESCALATION_KIND_STYLES[escalationEvent.kind];
            const kickerStyle = ESCALATION_KICKER_STYLES[kindStyle.tone];
            return (
              <div
                key={`${escalationEvent.dateLabel}-${escalationEvent.title}`}
                className="flex flex-col"
              >
                <div
                  className={`mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider ${kickerStyle.text}`}
                >
                  {escalationEvent.kind === GapTimelineEventKind.INTERA_FLAG ? (
                    <Flag
                      className="h-3 w-3 fill-[#00AEEF] text-[#00AEEF]"
                      strokeWidth={2}
                    />
                  ) : (
                    <span
                      className={`relative h-2 w-2 rounded-full ${kickerStyle.dot}`}
                    />
                  )}
                  {escalationEvent.dateLabel} · {kindStyle.label}
                </div>
                <h4 className="mb-1.5 text-[14px] font-bold leading-snug text-zinc-900">
                  {escalationEvent.title}
                </h4>
                <p className="mb-4 text-[12px] leading-relaxed text-zinc-500">
                  {renderInlineMarkdown(escalationEvent.description)}
                </p>

                <div className="mt-auto flex flex-wrap gap-1.5">
                  {escalationEvent.sources
                    .filter(isBlueBridgeEvidence)
                    .map((sourceLink) => {
                      const badge = evidenceSourceBadge(sourceLink);
                      const BadgeIcon = badge.icon;
                      const sourceUrl = evidenceLinkUrl(sourceLink);
                      const badgeBody = (
                        <>
                          <span
                            className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded ${
                              badge.iconTone ?? ''
                            }`}
                          >
                            {badge.logoSrc ? (
                              <img
                                src={badge.logoSrc}
                                alt=""
                                className="h-3 w-3"
                              />
                            ) : (
                              BadgeIcon && <BadgeIcon className="h-2.5 w-2.5" />
                            )}
                          </span>
                          <span className="max-w-[220px] truncate text-[11px] font-medium text-zinc-600">
                            {sourceLink.label}
                          </span>
                        </>
                      );
                      return sourceUrl ? (
                        <a
                          key={sourceLink.label}
                          href={sourceUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200/60 bg-zinc-50 py-0.5 pe-2 ps-0.5 transition-colors hover:border-[#00AEEF]/40 hover:bg-[#00AEEF]/5"
                        >
                          {badgeBody}
                        </a>
                      ) : (
                        <span
                          key={sourceLink.label}
                          className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200/60 bg-zinc-50 py-0.5 pe-2 ps-0.5"
                        >
                          {badgeBody}
                        </span>
                      );
                    })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/**
 * Long-form project background as a whole-card collapsible, closed by
 * default so it stays clearly secondary to the finding itself. The
 * enriched context is prose paragraphs separated by blank lines.
 */
function ProjectContextAccordion({
  project,
  fullProjectContext,
}: {
  project: DealIntelligenceProject;
  fullProjectContext: string;
}) {
  const contextParagraphs = fullProjectContext
    .split(/\n{2,}/)
    .map((contextParagraph) => contextParagraph.trim())
    .filter(Boolean);

  return (
    <section>
      <details className="group overflow-hidden rounded-xl border-2 border-zinc-200 bg-transparent">
        <summary className="flex cursor-pointer select-none list-none items-center justify-between px-6 py-3 focus:outline-none lg:px-8 lg:py-4 [&::-webkit-details-marker]:hidden">
          <span className="truncate pe-4 text-[19px] font-semibold text-[#0E1B3A] transition-opacity group-hover:opacity-75">
            Full project context - {projectDisplayName(project)}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-zinc-400 transition-transform duration-300 group-open:rotate-180" />
        </summary>
        <div className="px-6 pb-6 text-[16px] leading-relaxed text-zinc-600 lg:px-8 lg:pb-8">
          {contextParagraphs.map((contextParagraph, paragraphIndex) => (
            <p
              key={contextParagraph.slice(0, 40)}
              className={paragraphIndex === 0 ? 'pt-1' : 'pt-4'}
            >
              {renderInlineMarkdown(contextParagraph)}
            </p>
          ))}
        </div>
      </details>
    </section>
  );
}

/* ───────────────────────── Detail page shell ──────────────────────── */

export function GapDetail({
  flatGap,
  onBack,
  showProjectLink,
  language,
  onLanguageChange,
  interactive = true,
}: {
  flatGap: { gap: DetailGap; project: DealIntelligenceProject };
  onBack: () => void;
  /** Hidden on the project-scoped page, where the link would be circular. */
  showProjectLink: boolean;
  language: GapLanguage;
  onLanguageChange: (language: GapLanguage) => void;
  /**
   * False on the gap-review preview, which renders an unpublished draft. The
   * feedback row and its two mutations are then not rendered at all, so no
   * request can be issued against an id that is not in `gaps`. Defaults to true
   * so the customer page is unchanged.
   */
  interactive?: boolean;
}) {
  const { project } = flatGap;
  const gap = localizeGap(flatGap.gap, language);
  const { severity } = flatGap.gap;

  return (
    <div className={DETAIL_WIDTH}>
      <nav className="flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={onBack}
          className="group inline-flex cursor-pointer items-center gap-2 text-sm font-semibold text-zinc-500 transition-colors hover:text-[#0E1B3A]"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1 rtl:rotate-180 rtl:group-hover:translate-x-1" />
          Back to gaps
        </button>
        {SHOW_LANGUAGE_TOGGLE && (
          <LanguageToggle language={language} onChange={onLanguageChange} />
        )}
      </nav>

      <header className="mt-12">
        {gap.title && (
          <h1 className="text-4xl font-semibold leading-tight tracking-tight text-[#0E1B3A] md:text-5xl">
            {gap.title}
          </h1>
        )}
        <div
          className={`flex flex-wrap items-start justify-center gap-12 md:gap-24 ${
            gap.title ? 'mt-8' : ''
          }`}
        >
          <div className="flex flex-col items-center">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-400">
              Confidence
            </p>
            <div className="mt-1.5">
              {severity === undefined ? (
                // A draft has no severity, and banding a missing number would
                // print a confident "Low" for a gap nobody has scored.
                <span className="text-sm font-semibold text-zinc-400">
                  not assigned
                </span>
              ) : (
                <RankInline rank={gapRank(severity)} />
              )}
            </div>
          </div>
          <div className="flex flex-col items-center">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-400">
              Project
            </p>
            {showProjectLink ? (
              <Link
                to="/deal-intelligence/project/$projectId"
                params={{ projectId: project.externalId }}
                search={{ lastGap: gap._id }}
                title="View all gaps in this project"
                className={`group mt-1.5 inline-flex items-center gap-1.5 text-sm font-semibold ${LINK_CLASS}`}
              >
                {projectDisplayName(project)}
                <ArrowUpRight className="h-3.5 w-3.5 text-[#0089bd]/70 transition-colors group-hover:text-[#0089bd] rtl:-scale-x-100" />
              </Link>
            ) : (
              // Already on the project's route - closing the detail IS the
              // "view all gaps in this project" action.
              <button
                type="button"
                onClick={onBack}
                title="View all gaps in this project"
                className={`group mt-1.5 inline-flex cursor-pointer items-center gap-1.5 text-sm font-semibold ${LINK_CLASS}`}
              >
                {projectDisplayName(project)}
                <ArrowUpRight className="h-3.5 w-3.5 text-[#0089bd]/70 transition-colors group-hover:text-[#0089bd] rtl:-scale-x-100" />
              </button>
            )}
          </div>
          <div className="flex flex-col items-center">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-400">
              Type
            </p>
            <div className="mt-1.5">
              <CategoryInline category={gap.category} />
            </div>
          </div>
        </div>
      </header>

      <div className="mt-8 flex flex-col gap-10 pb-16">
        <OverviewSection gap={gap} interactive={interactive} />

        <ImpactActionsSection gap={gap} />

        {gap.fullProjectContext && (
          <ProjectContextAccordion
            project={project}
            fullProjectContext={gap.fullProjectContext}
          />
        )}
      </div>
    </div>
  );
}

/* ───────────────────────────── Page shell ─────────────────────────── */

/**
 * The nearest scrolling ancestor of an element, or null when the page itself
 * scrolls. The dashboard shell scrolls an inner `<main>` rather than the
 * window, so resetting scroll has to target that element - `window.scrollTo`
 * is a no-op inside it.
 */
function scrollableAncestor(element: HTMLElement | null): HTMLElement | null {
  let ancestor = element?.parentElement ?? null;
  while (ancestor) {
    const { overflowY } = getComputedStyle(ancestor);
    if (overflowY === 'auto' || overflowY === 'scroll') return ancestor;
    ancestor = ancestor.parentElement;
  }
  return null;
}

export function DealIntelligencePage({
  projectExternalId,
  gapId,
  lastViewedGapId,
}: {
  /** When set, the page shows only that project's gaps (the project page). */
  projectExternalId?: string;
  /** When set, the page shows that gap's detail view (the gap page). */
  gapId?: string;
  /**
   * The gap the reader just came back from (from the `lastGap` search param).
   * The feed scrolls to that card and flashes a ring around it so they can
   * pick up where they left off.
   */
  lastViewedGapId?: string;
}) {
  const { data, isLoading, isError } = useDealIntelligence();
  const navigate = useNavigate();
  const lastViewedCardRef = useRef<HTMLElement | null>(null);
  const pageRootRef = useRef<HTMLDivElement | null>(null);
  const [highlightedGapId, setHighlightedGapId] = useState<string | null>(null);
  // Guards against re-running the restore on every refetch - it should fire
  // once per return trip, not whenever the query data settles again.
  const restoredGapIdRef = useRef<string | null>(null);
  const [gapLanguage, setGapLanguage] =
    useState<GapLanguage>(storedGapLanguage);
  // Force English when the toggle is hidden, so no stale LT selection lingers.
  const activeLanguage: GapLanguage = SHOW_LANGUAGE_TOGGLE ? gapLanguage : 'en';

  const selectGapLanguage = (language: GapLanguage) => {
    setGapLanguage(language);
    persistGapLanguage(language);
  };

  const openGapRoute = (selectedGapId: string) => {
    if (projectExternalId) {
      navigate({
        to: '/deal-intelligence/project/$projectId/gap/$gapId',
        params: { projectId: projectExternalId, gapId: selectedGapId },
      });
    } else {
      navigate({
        to: '/deal-intelligence/gap/$gapId',
        params: { gapId: selectedGapId },
      });
    }
  };

  // Going back hands the gap id to the list as `lastGap`, which is what lets
  // the feed restore the reader's place.
  const closeGapRoute = () => {
    if (projectExternalId) {
      navigate({
        to: '/deal-intelligence/project/$projectId',
        params: { projectId: projectExternalId },
        search: { lastGap: gapId },
      });
    } else {
      navigate({ to: '/deal-intelligence', search: { lastGap: gapId } });
    }
  };

  useEffect(() => {
    if (!gapId) return;
    const closeOnEscape = (keyboardEvent: KeyboardEvent) => {
      if (keyboardEvent.key === 'Escape') closeGapRoute();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [gapId, projectExternalId]);

  useEffect(() => {
    // Opening a gap always starts at the top. Landing on the list with a
    // `lastGap` is the one case where the restore effect below owns the
    // scroll position instead.
    if (!gapId && lastViewedGapId) return;
    const scrollContainer = scrollableAncestor(pageRootRef.current);
    if (scrollContainer) {
      scrollContainer.scrollTop = 0;
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [gapId, lastViewedGapId]);

  useEffect(() => {
    if (gapId) {
      // Back on a detail view - arm the restore for the next return trip.
      restoredGapIdRef.current = null;
      return;
    }
    if (!lastViewedGapId || restoredGapIdRef.current === lastViewedGapId)
      return;
    const lastViewedCard = lastViewedCardRef.current;
    if (!lastViewedCard) return;

    restoredGapIdRef.current = lastViewedGapId;
    lastViewedCard.scrollIntoView({ block: 'center', behavior: 'smooth' });
    setHighlightedGapId(lastViewedGapId);
    const clearHighlight = setTimeout(
      () => setHighlightedGapId(null),
      GAP_HIGHLIGHT_DURATION_MS
    );
    return () => clearTimeout(clearHighlight);
    // `data` is a dependency because the cards - and so the ref - only exist
    // once the query has resolved.
  }, [gapId, lastViewedGapId, data]);

  const scopedProject = projectExternalId
    ? data?.projects.find((project) => project.externalId === projectExternalId)
    : undefined;
  const visibleProjects = scopedProject
    ? [scopedProject]
    : data?.projects ?? [];
  const flatGaps = flattenGaps(visibleProjects);
  const openGap = flatGaps.find((flatGap) => flatGap.gap._id === gapId);

  return (
    <div
      ref={pageRootRef}
      className="min-h-screen bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_60%,#f3f6fb_100%)]"
    >
      <DealIntelligenceSidebar />
      <div className={SIDEBAR_WIDTH_CLASS}>
        {/* The glow anchors to the content region (not the viewport), so it
            stays centered over the column despite the fixed rail. */}
        <div className="relative isolate px-2.5 py-10 md:px-5 md:py-14">
          <div className="pointer-events-none absolute inset-x-0 -top-24 -z-10 h-72 bg-[radial-gradient(58%_100%_at_50%_0%,rgba(0,174,239,0.07),transparent_72%)]" />
          {isLoading && (
            <div className="flex min-h-[50vh] items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-zinc-300" />
            </div>
          )}
          {isError && (
            <div className="mx-auto max-w-md rounded-3xl border border-rose-100 bg-rose-50 p-8 text-center">
              <p className="text-sm font-semibold text-rose-700">
                Couldn&apos;t load the gap library.
              </p>
              <p className="mt-1 text-sm text-rose-600">
                Check that the API is running, then reload.
              </p>
            </div>
          )}
          {data &&
            (openGap ? (
              <GapDetail
                flatGap={openGap}
                onBack={closeGapRoute}
                showProjectLink={!projectExternalId}
                language={activeLanguage}
                onLanguageChange={selectGapLanguage}
              />
            ) : (
              <GapFeed
                flatGaps={flatGaps}
                scopedProject={scopedProject}
                language={activeLanguage}
                onLanguageChange={selectGapLanguage}
                onOpen={openGapRoute}
                lastViewedGapId={lastViewedGapId}
                lastViewedCardRef={lastViewedCardRef}
                highlightedGapId={highlightedGapId}
              />
            ))}
        </div>
      </div>
    </div>
  );
}
